/**
 * Advanced Socket Selection Component
 * 
 * A collapsible section for selecting or creating sockets when connecting integrations.
 * This is used in the "Advanced" section of integration modals.
 */

import { useState, useEffect } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Server, Plus, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { apiService } from '../../services/api';
import { IntegrationDefinition } from '../../integrations/types';
import { SpaceSocket } from '../../services/api';

interface AdvancedSocketSelectionProps {
  integration: IntegrationDefinition;
  spaceId: string;
  currentSocket: SpaceSocket | null;
  onSocketChange: (socket: SpaceSocket) => void;
  disabled?: boolean;
}

export function AdvancedSocketSelection({
  integration,
  spaceId,
  currentSocket,
  onSocketChange,
  disabled = false,
}: AdvancedSocketSelectionProps) {
  const [sockets, setSockets] = useState<SpaceSocket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadSockets();
  }, [spaceId, integration]);

  const loadSockets = async () => {
    try {
      setLoading(true);
      setError(null);
      const socketList = await apiService.getSocketsForSpace(spaceId);
      
      // Filter compatible sockets
      const compatibleSockets = socketList.sockets.filter(socket => {
        const socketType = socket.socket_config?.template_id;
        return integration.socketTypes.includes(socketType as any);
      });
      
      // Sort: default sockets first, then others
      compatibleSockets.sort((a, b) => {
        const aIsDefault = a.socket_name.toLowerCase().includes('default');
        const bIsDefault = b.socket_name.toLowerCase().includes('default');
        if (aIsDefault && !bIsDefault) return -1;
        if (!aIsDefault && bIsDefault) return 1;
        return a.socket_name.localeCompare(b.socket_name);
      });
      
      setSockets(compatibleSockets);
    } catch (err) {
      console.error('Failed to load sockets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sockets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSocket = async (socketType: string) => {
    if (!spaceId || !integration) return;
    
    setIsCreating(true);
    setError(null);
    
    try {
      const socketName = `${integration.name} ${socketType.toUpperCase()} Socket`;
      const socketDescription = `Socket for ${integration.name} integration using ${socketType.toUpperCase()} protocol`;
      
      const socket = await apiService.createSocketFromTemplate(
        spaceId,
        socketName,
        socketDescription,
        socketType
      );
      
      // Reload sockets to include the new one
      await loadSockets();
      
      // Get the updated socket list to find the newly created socket
      const socketList = await apiService.getSocketsForSpace(spaceId);
      const newSocket = socketList.sockets.find(s => s.socket_id === socket.socket_id) || socket;
      onSocketChange(newSocket);
    } catch (err) {
      console.error('Failed to create socket:', err);
      setError(err instanceof Error ? err.message : 'Failed to create socket');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelectSocket = (socket: SpaceSocket) => {
    onSocketChange(socket);
  };

  const getSocketTypeIcon = (_socketType: string) => {
    return <Server className="w-4 h-4 text-slate-500" />;
  };

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="socket-selection" className="border border-gray-200 rounded-lg">
        <AccordionTrigger className="px-4 py-3 hover:no-underline">
          <div className="flex items-center justify-between w-full pr-2">
            <div className="flex items-center gap-2">
              <Server className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Advanced: Socket Selection</span>
            </div>
            {currentSocket && (
              <Badge variant="outline" className="text-xs">
                {currentSocket.socket_name}
              </Badge>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <div className="space-y-4">
            <p className="text-xs text-gray-600">
              By default, integrations use the default socket for their type. Use this section to select a different socket or create a new one.
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                {/* Show message if no socket is selected yet */}
                {!currentSocket && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
                      <p className="text-xs text-yellow-700">
                        No socket selected. Please select an existing socket or create a new one below.
                      </p>
                    </div>
                  </div>
                )}

                {/* Existing Compatible Sockets */}
                {sockets.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-medium text-gray-700 mb-2">Select Existing Socket</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {sockets.map((socket) => {
                        const isSelected = currentSocket?.socket_id === socket.socket_id;
                        const isDefault = socket.socket_name.toLowerCase().includes('default');
                        
                        return (
                          <Card
                            key={socket.socket_id}
                            className={`cursor-pointer transition-all ${
                              isSelected
                                ? 'border-2 border-indigo-500 bg-indigo-50'
                                : 'border border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => !disabled && handleSelectSocket(socket)}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {getSocketTypeIcon(socket.socket_config?.template_id || '')}
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-gray-900">
                                        {socket.socket_name}
                                      </span>
                                      {isDefault && (
                                        <Badge variant="outline" className="text-xs bg-blue-50">
                                          Default
                                        </Badge>
                                      )}
                                      {isSelected && (
                                        <CheckCircle className="w-4 h-4 text-indigo-600" />
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-500">
                                      {socket.socket_config?.template_id?.toUpperCase()} • {socket.integration_bindings?.length || 0} integration{socket.integration_bindings?.length !== 1 ? 's' : ''}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Create New Socket */}
                <div className="space-y-2 pt-2 border-t border-gray-200">
                  <h4 className="text-xs font-medium text-gray-700 mb-2">Create New Socket</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {integration.socketTypes.map((socketType) => (
                      <Button
                        key={socketType}
                        variant="outline"
                        size="sm"
                        onClick={() => handleCreateSocket(socketType)}
                        disabled={disabled || isCreating}
                        className="text-xs"
                      >
                        {isCreating ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <Plus className="w-3 h-3 mr-1" />
                        )}
                        New {socketType.toUpperCase()} Socket
                      </Button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

